import { Box, InputLabel, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CountrySelectorDropdown,
  FlagImage,
} from 'react-international-phone';
import 'react-international-phone/style.css';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useWindowDimension } from '../../hooks/useWindowDimension';
import { BREAKPOINTS, MOBILE_NUMBER_INPUT } from '../../consts/constants';
import { Styles } from './LoginFormStyles';
import { HaleLoginFormStyles } from './HaleLoginFormStyles';
import './HaleCountryDropdown.css';
import ErrIcon from '../../assets/icons/error.svg';

interface ILoginForm {
  value: string;
  onChange: (value: string) => void;
  isPhoneValid: boolean;
  onCountryCodeChange: (isd: string) => void;
  variant?: 'default' | 'hale';
}

const DEFAULT_COUNTRIES = {
  default: { iso2: 'in', dialCode: '91' },
  hale: { iso2: 'ae', dialCode: '971' },
};

const LoginForm = ({
  value,
  onChange,
  isPhoneValid,
  onCountryCodeChange,
  variant = 'default',
}: ILoginForm) => {
  const isHaleVariant = variant === 'hale';
  const defaultCountry = isHaleVariant
    ? DEFAULT_COUNTRIES.hale
    : DEFAULT_COUNTRIES.default;

  const [isTouched, setIsTouced] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [openDropdown, setOpenDropdown] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const [width] = useWindowDimension();

  const formContainerRef = useRef<HTMLDivElement>(null);

  const styles = isHaleVariant ? HaleLoginFormStyles(theme) : Styles(theme);
  const isXs = width <= BREAKPOINTS.xs;

  const getBorderColor = (): string => {
    if (isHaleVariant) {
      if (isTouched && errorMsg) return '#F54C4C';
      return '#e2e8f0';
    }
    if (isTouched && errorMsg) return theme.palette.quinary?.red_400 ?? '';
    return theme.custom.customColors['GREY_800'] ?? '';
  };

  useEffect(() => {
    onCountryCodeChange(`+${defaultCountry.dialCode}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!value) {
      setErrorMsg(null);
      return;
    }

    if (isPhoneValid) {
      setErrorMsg(null);
      return;
    }

    if (value.length >= 1) {
      setErrorMsg('login.mobileNo.invalid');
    }
  }, [value, isPhoneValid]);

  const handleCountryChange = (newCountry: {
    iso2: string;
    dialCode: string;
  }) => {
    setSelectedCountry({
      iso2: newCountry.iso2,
      dialCode: newCountry.dialCode,
    });
    onCountryCodeChange(`+${newCountry.dialCode}`);
    setOpenDropdown(false);
    onChange('');
  };

  const handleInputChange = (inputValue: string) => {
    const filtered = inputValue
      .split('')
      .filter((char) => MOBILE_NUMBER_INPUT.SINGLE_CHAR_REGEX.test(char))
      .join('')
      .slice(0, MOBILE_NUMBER_INPUT.MAX_LENGTH);

    onChange(filtered);
  };

  return (
    <Box sx={styles.container}>
      {width >= BREAKPOINTS.xs && (
        <InputLabel sx={styles.label}>{t('login.mobileNo')}</InputLabel>
      )}
      <Box
        ref={formContainerRef}
        sx={{
          ...styles.formContainer,
          position: 'relative',
          ...(isHaleVariant &&
            isTouched &&
            errorMsg && {
              borderColor: '#F54C4C',
              boxShadow: 'none',
            }),
        }}
      >
        <Box
          className="flag-and-code"
          onClick={() => setOpenDropdown(!openDropdown)}
          sx={{
            ...styles.flagCodeContainer,
            ...(isHaleVariant && openDropdown && {
              backgroundColor: '#ffffff',
            }),
            ...(isHaleVariant ? {} : { borderColor: getBorderColor() }),
          }}
        >
          <Box sx={styles.flagCode}>
            <FlagImage iso2={selectedCountry.iso2} style={styles.flag} />

            <span className="country-code">
              + {selectedCountry.dialCode}
            </span>
          </Box>

          <Box>
            <KeyboardArrowDownIcon
              sx={{
                color: isHaleVariant ? '#64748b' : theme.palette.text.septenary,
                ...(isHaleVariant && {
                  transform: openDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }),
              }}
            />
          </Box>

          <CountrySelectorDropdown
            show={openDropdown}
            selectedCountry={selectedCountry.iso2}
            onSelect={(newCountry) => handleCountryChange(newCountry)}
            className={isHaleVariant ? 'hale-country-dropdown' : undefined}
            listItemClassName={
              isHaleVariant ? 'hale-country-dropdown__item' : undefined
            }
            listItemSelectedClassName={
              isHaleVariant ? 'hale-country-dropdown__item--selected' : undefined
            }
            listItemFocusedClassName={
              isHaleVariant ? 'hale-country-dropdown__item--focused' : undefined
            }
            listItemCountryNameClassName={
              isHaleVariant ? 'hale-country-dropdown__item-name' : undefined
            }
            listItemDialCodeClassName={
              isHaleVariant ? 'hale-country-dropdown__item-dial' : undefined
            }
            listItemFlagClassName={
              isHaleVariant ? 'hale-country-dropdown__item-flag' : undefined
            }
            style={{
              ...styles.countryDropdown,
              width: isHaleVariant
                ? 'min(22rem, calc(100vw - 2rem))'
                : formContainerRef?.current?.offsetWidth
                  ? `${formContainerRef.current.offsetWidth}px`
                  : theme.custom.width['100%'],
              position: 'absolute',
              overflowY: 'auto',
              top: isXs ? 'auto' : isHaleVariant ? 'calc(100% + 0.5rem)' : '105%',
              bottom: isXs ? '105%' : 'auto',
              scrollbarWidth: isHaleVariant ? 'thin' : 'none',
              zIndex: 20,
            }}
            onClose={() => {
              setOpenDropdown(false);
            }}
          />
        </Box>
        <input
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={t('login.mobileNo')}
          style={{
            ...styles.inputStyle,
            ...(isHaleVariant ? {} : { borderColor: getBorderColor() }),
          }}
          onFocus={() => setIsTouced(false)}
          onBlur={() => setIsTouced(true)}
        />
      </Box>
      {isTouched && errorMsg && (
        <Typography
          sx={styles.error}
          style={{
            textTransform: 'uppercase',
          }}
        >
          <img src={ErrIcon} alt="error" />

          {t(errorMsg)}
        </Typography>
      )}
    </Box>
  );
};

export default LoginForm;
